import axios from 'axios';
import { getPartnerToken } from '@/services/auth/partnerSessionStorage';
import { OFFLINE_MODE_ENABLED } from '@/services/offline/offlineFeature';
import { reactive, readonly } from 'vue';
import {
  getSyncState,
  saveSyncState,
} from '@/services/localDb';
import { getOrCreateDeviceId } from '@/services/localDb/device';
import {
  downloadRestaurantOfflineData,
  getActiveRestaurantContext,
  sanitizeRestaurantBackupModuleData,
} from '@/services/offline/restaurantOfflineData';
import {
  isBackendUnavailableError,
  markBackendAvailable,
  markBackendUnavailable,
} from '@/services/connectivity/connectivityService';

const BACKUP_PREFIX = 'restaurant_backup';

let backupInProgress = false;

const backupState = reactive({
  running: false,
  lastStartedAt: null,
  lastFinishedAt: null,
  lastSuccessAt: null,
  lastErrorAt: null,
  lastError: '',
  lastMessage: '',
  visibleUntil: 0,
});

function showBackupNotice(partialState) {
  Object.assign(backupState, {
    ...partialState,
    visibleUntil: Date.now() + 6000,
  });
}

const BACKUP_MODULES = [
  {
    id: 'restaurant_core',
    label: 'Configuración, salones y carta',
    endpoints: [
      ['restaurant_config', 'get', '/v1/api/partner/get_restaurant_config'],
      ['tpvs', 'get', '/v1/api/partner/get_tpvs_list'],
      ['pos_bootstrap', 'get', '/v1/api/app/pos/bootstrap'],
      ['pos_catalog', 'get', '/v1/api/app/pos/catalog'],
      ['allergens', 'get', '/v1/api/app/getAllergens'],
    ],
  },
  {
    id: 'reservations',
    label: 'Reservas',
    endpoints: [
      ['reservation_settings', 'get', '/v1/api/partner/reservation_settings'],
      ['reservation_tables', 'get', '/v1/api/partner/reservation_tables'],
      ['reservations', 'get', '/v1/api/partner/reservations'],
    ],
  },
  {
    id: 'cash',
    label: 'Caja',
    endpoints: [
      ['current_cash_session', 'get', '/v1/api/app/GetCurrentCashSession'],
      ['cash_sessions', 'get', '/v1/api/app/GetCashSessions'],
      ['cash_movements', 'get', '/v1/api/app/GetCashMovements'],
      ['dashboard', 'get', '/v1/api/app/getDashboard'],
    ],
  },
  {
    id: 'staff',
    label: 'Personal',
    endpoints: [
      ['users', 'get', '/v1/api/partner/getUsers'],
    ],
  },
  {
    id: 'invoices',
    label: 'Facturas',
    endpoints: [
      ['invoice_settings', 'get', '/v1/api/app/GetInvoiceSettings'],
      ['invoice_candidates', 'get', '/v1/api/app/GetInvoiceCandidates'],
      ['invoices', 'get', '/v1/api/app/GetInvoices'],
    ],
  },
  {
    id: 'frequent_customers',
    label: 'Clientes habituales',
    endpoints: [
      ['frequent_customers', 'get', '/v1/api/app/frequent-customers'],
    ],
  },
  {
    id: 'print_templates',
    label: 'Plantillas de impresion',
    endpoints: [
      ['templates', 'get', '/v1/api/partner/print-templates'],
    ],
  },
  {
    id: 'orders_snapshot',
    label: 'Comandas online actuales',
    endpoints: [
      ['active_orders', 'get', '/v1/api/app/ActiveOrders'],
      ['kitchen_state', 'get', '/v1/api/partner/getKitchenState'],
      ['orders_history_today', 'get', '/v1/api/app/orders/history', () => ({
        from_date: localDateString(),
        to_date: localDateString(),
      })],
    ],
  },
];

function nowIso() {
  return new Date().toISOString();
}

function localDateString(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function backupKey(restaurantId, moduleId) {
  return `${BACKUP_PREFIX}:${restaurantId}:${moduleId}`;
}

function backupIndexKey(restaurantId) {
  return `${BACKUP_PREFIX}:${restaurantId}:index`;
}

function apiBaseUrl() {
  return process.env.VUE_APP_API_URL || '';
}

function authToken() { return getPartnerToken(); }

function authHeaders() {
  const token = authToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchEndpoint(endpoint) {
  const [key, method, path, paramsDefinition] = endpoint;
  const params = typeof paramsDefinition === 'function' ? paramsDefinition() : paramsDefinition;
  const response = await axios.request({
    url: `${apiBaseUrl()}${path}`,
    method,
    headers: authHeaders(),
    params,
    timeout: 8000,
  });

  return [key, response.data];
}

async function backupModule(restaurantId, moduleDefinition) {
  const existingSnapshot = await getSyncState(backupKey(restaurantId, moduleDefinition.id));
  const results = await Promise.allSettled(moduleDefinition.endpoints.map(fetchEndpoint));
  const data = {};
  const errors = [];

  results.forEach((result, index) => {
    const endpointKey = moduleDefinition.endpoints[index][0];
    if (result.status === 'fulfilled') {
      const [key, value] = result.value;
      data[key] = value;
      return;
    }

    errors.push({
      endpoint: endpointKey,
      message: result.reason?.message || String(result.reason),
      backend_unavailable: isBackendUnavailableError(result.reason),
      status: result.reason?.response?.status || null,
    });
  });

  const mergedData = errors.length
    ? {
      ...((existingSnapshot?.data && typeof existingSnapshot.data === 'object') ? existingSnapshot.data : {}),
      ...data,
    }
    : data;
  const sanitizedData = sanitizeRestaurantBackupModuleData(moduleDefinition.id, mergedData, restaurantId);
  const savedAt = nowIso();

  const snapshot = {
    module_id: moduleDefinition.id,
    label: moduleDefinition.label,
    restaurant_id: restaurantId,
    device_id: getOrCreateDeviceId(),
    saved_at: savedAt,
    last_success_at: errors.length ? (existingSnapshot?.last_success_at || existingSnapshot?.saved_at || null) : savedAt,
    has_partial_failures: errors.length > 0,
    data: sanitizedData,
    errors,
  };

  await saveSyncState(backupKey(restaurantId, moduleDefinition.id), snapshot);
  return snapshot;
}

export async function saveRestaurantBackupSnapshot(restaurantId, moduleId, data, options = {}) {
  if (!OFFLINE_MODE_ENABLED) return null;
  if (!restaurantId || !moduleId) {
    return null;
  }

  const sanitizedData = sanitizeRestaurantBackupModuleData(moduleId, data, restaurantId);

  const snapshot = {
    module_id: moduleId,
    label: options.label || moduleId,
    restaurant_id: restaurantId,
    device_id: options.device_id || getOrCreateDeviceId(),
    saved_at: options.saved_at || nowIso(),
    data: sanitizedData,
    errors: Array.isArray(options.errors) ? options.errors : [],
  };

  await saveSyncState(backupKey(restaurantId, moduleId), snapshot);
  return snapshot;
}

export function getRestaurantBackupModules() {
  return BACKUP_MODULES.map(({ id, label }) => ({ id, label }));
}

export const restaurantBackupState = readonly(backupState);

export async function runRestaurantFullBackup() {
  if (!OFFLINE_MODE_ENABLED) throw new Error('La copia offline está desactivada.');
  if (backupInProgress) {
    return getRestaurantBackupStatus();
  }

  backupInProgress = true;
  showBackupNotice({
    running: true,
    lastStartedAt: nowIso(),
    lastMessage: 'Actualizando copia local del TPV...',
    lastError: '',
  });
  const context = await getActiveRestaurantContext();
  if (!context.restaurant_id) {
    backupInProgress = false;
    showBackupNotice({
      running: false,
      lastErrorAt: nowIso(),
      lastError: context.error || 'No hay restaurante activo para sincronizar datos locales.',
      lastMessage: '',
    });
    throw new Error(context.error || 'No hay restaurante activo para sincronizar datos locales.');
  }

  try {
    await downloadRestaurantOfflineData(context.restaurant_id);
    const snapshots = [];
    for (const moduleDefinition of BACKUP_MODULES) {
      snapshots.push(await backupModule(context.restaurant_id, moduleDefinition));
    }

    const summary = {
      restaurant_id: context.restaurant_id,
      restaurant_name: context.restaurant_name || '',
      device_id: getOrCreateDeviceId(),
      last_success_at: nowIso(),
      modules: snapshots.map((snapshot) => ({
        id: snapshot.module_id,
        label: snapshot.label,
        saved_at: snapshot.saved_at,
        errors: snapshot.errors,
      })),
    };

    await saveSyncState(backupIndexKey(context.restaurant_id), summary);
    await saveSyncState(`${BACKUP_PREFIX}:${context.restaurant_id}:meta`, {
      restaurant_id: context.restaurant_id,
      backup_started_at: snapshots[0]?.saved_at || summary.last_success_at,
      backup_finished_at: summary.last_success_at,
      backup_status: 'success',
      backup_error: '',
      dataset_counts: summary.modules.reduce((counts, moduleStatus) => {
        counts[moduleStatus.id] = moduleStatus.errors?.length ? 'partial' : 'ok';
        return counts;
      }, {}),
    });
    markBackendAvailable();
    showBackupNotice({
      running: false,
      lastFinishedAt: summary.last_success_at,
      lastSuccessAt: summary.last_success_at,
      lastMessage: `Copia local actualizada (${summary.modules.length} módulos).`,
      lastError: '',
    });
    return summary;
  } catch (error) {
    const fallbackContext = context.restaurant_id;
    if (fallbackContext) {
      await saveSyncState(`${BACKUP_PREFIX}:${fallbackContext}:meta`, {
        restaurant_id: fallbackContext,
        backup_started_at: nowIso(),
        backup_finished_at: nowIso(),
        backup_status: 'error',
        backup_error: error?.message || String(error),
        dataset_counts: {},
      });
    }
    if (isBackendUnavailableError(error)) {
      markBackendUnavailable(error?.message || 'No se pudo completar la copia local.');
    }
    showBackupNotice({
      running: false,
      lastFinishedAt: nowIso(),
      lastErrorAt: nowIso(),
      lastError: error?.message || String(error),
      lastMessage: '',
    });
    throw error;
  } finally {
    backupInProgress = false;
  }
}

export async function getRestaurantBackupStatus(restaurantId = null) {
  const context = restaurantId
    ? { restaurant_id: restaurantId }
    : await getActiveRestaurantContext();

  if (!context.restaurant_id) {
    return {
      restaurant_id: null,
      available: false,
      modules: [],
      error: context.error || 'No hay restaurante activo.',
    };
  }

  const summary = await getSyncState(backupIndexKey(context.restaurant_id));
  const modules = await Promise.all(BACKUP_MODULES.map(async (moduleDefinition) => {
    const snapshot = await getSyncState(backupKey(context.restaurant_id, moduleDefinition.id));
    return {
      id: moduleDefinition.id,
      label: moduleDefinition.label,
      available: Boolean(snapshot),
      saved_at: snapshot?.saved_at || null,
      errors: snapshot?.errors || [],
    };
  }));

  return {
    restaurant_id: context.restaurant_id,
    restaurant_name: summary?.restaurant_name || context.restaurant_name || '',
    available: modules.some((moduleStatus) => moduleStatus.available),
    last_success_at: summary?.last_success_at || null,
    modules,
    error: '',
  };
}

export async function getRestaurantBackupSnapshot(restaurantId, moduleId) {
  if (!restaurantId || !moduleId) return null;
  return getSyncState(backupKey(restaurantId, moduleId));
}
